/**
 * Remembering which ad a visitor came from.
 *
 * Google puts a `gclid` on the landing URL of every ad click (`gbraid` /
 * `wbraid` on iOS app and web-to-app clicks, where a gclid cannot be set).
 * It is on the URL for exactly one pageview and then gone — the visitor
 * navigates, the query string disappears, and by the time they actually
 * request a quote there is nothing left to say where they came from.
 *
 * So it is copied into a first-party cookie on arrival and travels with the
 * quote. Two things need it:
 *
 *   NOW     nothing. Google's own tag handles click attribution through its
 *           own cookie, as long as consent allows one.
 *   LATER   the offline conversion upload. A quote becomes a real job days
 *           later, for real money, and the only way to tell Google Ads "that
 *           click was worth $3,200" is to hand back the click id it issued.
 *           Without capturing it now, that is unrecoverable.
 *
 * 90 days matches Google Ads' default conversion window: a click older than
 * the window cannot be credited anyway, so keeping it longer only holds data
 * for no purpose.
 */

const COOKIE = "aa_click";
const MAX_AGE_DAYS = 90;

/** Click identifiers, in the order Google prefers them. */
const PARAMS = ["gclid", "gbraid", "wbraid"] as const;

export type ClickId = { param: string; value: string; at: string };

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const hit = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
}

/**
 * Copy a click id off the current URL, if there is one.
 *
 * The FIRST click wins for the life of the cookie... except it does not: a
 * later click is the one Google will credit, so a fresh id overwrites. What
 * must not happen is an ordinary pageview with no id wiping the one we have.
 */
export function captureClickId(search: string = typeof window === "undefined" ? "" : window.location.search): ClickId | null {
  if (typeof document === "undefined") return null;
  const params = new URLSearchParams(search);

  for (const param of PARAMS) {
    const value = params.get(param);
    if (!value) continue;
    const record: ClickId = { param, value, at: new Date().toISOString() };
    try {
      document.cookie =
        `${COOKIE}=${encodeURIComponent(JSON.stringify(record))}; ` +
        `path=/; max-age=${MAX_AGE_DAYS * 24 * 60 * 60}; SameSite=Lax` +
        (location.protocol === "https:" ? "; Secure" : "");
    } catch {
      // Cookies blocked. Attribution is lost for this visitor, which is
      // their choice to make; nothing else should break.
    }
    return record;
  }
  return null;
}

/** The click that brought this visitor here, if we still hold one. */
export function storedClickId(): ClickId | null {
  const raw = readCookie(COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ClickId;
    return parsed?.value ? parsed : null;
  } catch {
    return null;
  }
}
